import { Relation } from '@common/types';
import {
  HealthAddOn,
  HealthPremium,
  HealthProduct,
  HealthQuotePlanDetails
} from '@store/healthquote';

export interface InsuredBasic extends Record<string, any> {
  member_id: string;
  full_name: string;
  relation: Relation;
  dob: string;
}

export interface WithInsuredBasic extends Record<string, any> {
  insured_basic: InsuredBasic[];
}

export enum HealthPolicyStatus {
  PRODUCT_FINALISED = 'Finalised Product',
  KYC_DONE = 'KYC Done',
  FORM_WIP = 'ProposalForm WIP',
  FORM_COMPLETED = 'ProposalForm Completed',
  FORM_SUBMITTED = 'UW Submitted',
  PAYMENT_DONE = 'Payment Done',
  PAYMENT_FAILED = 'Payment Failed',
  FINAL_PUSH_SUBMITTED = 'FinalPush Submitted',
  CKYC_APPROVED = 'approved',
  CKYC_PENDING = 'pending',
  CKYC_REJECTED = 'rejected',
  CKYC_FAILED = 'failed',
  ISSUED = 'Policy Issued',
  REJECTED = 'Policy Rejected',
  CANCELLED = 'Policy Cancelled',
  PAYMENT_ON_HOLD = 'Payment on Hold',
  LOADING_OR_CO_RAISED = 'Loading/CO Raised',
  LOADING_OR_CO_CLOSED = 'Loading/CO Closed',
  LOADING_PAYMENT_DONE = 'Loading Payment Done',
  LOADING_PAYMENT_FAILED = 'Loading Payment Failed',
  LOADING_PAYMENT_HOLD = 'Loading Payment Hold'
}

export enum HealthStepStatus {
  ACTIVE = 'active',
  PENDING = 'pending',
  COMPLETED = 'completed'
}

export type StepDocument = {
  title: string;
  link: string;
};

export type Step = {
  pageHeading: string;
  pageCaption: string;
  title: string;
  subTitle: string;
  minToComplete: number;
  btnLabel: string;
  btnLink: string;
  isPaymentStep?: boolean;
  showCounterOfferModal?: boolean;
  step: string;
  status: HealthStepStatus;
  isBDPaymentEnabled?: boolean;
  bdPaymentData?: any;
  documents?: StepDocument[];
  preCTABtn?: {
    link: string;
    label: string;
  };
};

export type HealthStatusBoundary = {
  product: HealthProduct;
  boundaries?: {
    planTypes?: PlanType[] | undefined;
    discounts?: any[] | undefined;
  };
};

type ProposalForm = {
  [key: string]: any;
};

interface Address {
  city: string;
  line1: string;
  line2: string;
  pincode: string;
  state: string;
}

export interface CkycInfo {
  dob: string;
  pan: string | null;
  name: string;
  email: string;
  error: string;
  status: string;
  ckyc_number: string;
  kyc_verified: boolean;
  current_address: Address;
  permanent_address: Address;
  rejectionReason: string;
}

export type AcceptedMember = {
  dob: string;
  name: string;
  gender: string;
};

export type LoadingDetails = {
  dob: string;
  name: string;
  gender: string;
  loading_percentage: string;
  reason_for_loading: string;
};

export type RejectedMember = {
  dob: string;
  name: string;
  gender: string;
  rejection_reason: string;
};

export type PreUwInfo = {
  is_stp: boolean;
  status: string;
  clientId: string;
  end_date: string;
  quote_id: string;
  login_date: string;
  start_date: string;
  quote_number: string;
  delta_premium: number;
  actual_premium: number;
  accepted_members: AcceptedMember[];
  loading_details: LoadingDetails[];
  rejected_members: RejectedMember[];
  is_policy_rejected: boolean;
  total_final_premium: string;
  policy_final_remarks: string | null;
};

export type AddInfo = {
  pre_uw_info: PreUwInfo;
  post_uw_info?: PreUwInfo;
};

export type LinkInfo = {
  rel: string;
  href: string;
  method: string;
  headers?: {
    authorization: string;
  };
  parameters?: {
    rdata: string;
    mercid: string;
    bdorderid: string;
  };
  valid_date?: string;
};

export type AdditionalInfo = {
  additional_info1: string;
  additional_info2: string;
  additional_info3: string;
  additional_info4: string;
  additional_info5: string;
  additional_info6: string;
  additional_info7: string;
  additional_info8: string;
  additional_info9: string;
  additional_info10: string;
};

export type BdPaymentConfig = {
  internal_id: string;
  order_id: string;
  bd_order_id: string;
  ordered_on: string;
  valid_till: string;
  amount: number;
  status: string;
  transaction_id: string | null;
  bank_ref_no: string | null;
  insurer_ref_no: string | null;
  links_info: LinkInfo[];
  additional_info: AdditionalInfo;
  post_transaction_info: Record<string, any>;
  refund_info: Record<string, any>;
  term_policy: any;
  health_policy: number;
};

export type HealthPolicyStatusResponse = {
  policy_no: string;
  addons: HealthAddOn[];
  app_no: string;
  ckyc_info: CkycInfo;
  kyc_info?: CkycInfo;
  issued_date: string;
  policy_id: string;
  premium: HealthPremium;
  product: string;
  product_details: HealthQuotePlanDetails & {
    plan_type?: string;
  };
  product_basic_details: HealthStatusBoundary;
  proposal_form: ProposalForm;
  renewal: boolean;
  status: string;
  steps: Step[];
  submitted_at: string;
  cis_doc_url?: string;
  url_error?: string;
  payment_form: any;
  add_info?: AddInfo;
  bd_payment_config?: BdPaymentConfig;
  co_letter_url?: string;
  is_expired?: boolean;
  expiry_reason?: string | null;
  expiring_message?: string;
  updated_at?: string;
};

export type CarePincodeDataResponse = {
  cities: string[];
  states: string[];
};

export type PlanTypeFeature = {
  name: string;
  value: string;
};

export type PlanType = {
  key: string;
  name: string;
  description: string;
  features: PlanTypeFeature[];
};

export type HealthRevisedPremiumInfo = {
  wasPremiumRevised: boolean;
  onViewRevisedPremium: () => void;
  revisedPremium: {
    deltaPremium: number;
    finalPremium: number;
    originalPremium: number;
  } | null;
};

export interface SharePolicyDocumentResponse {
  data: Record<string, string>;
  success: boolean;
}
